import React from "react";
import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-20" data-testid="terms-page">
      <div className="overline">LEGAL · TERMS &amp; CONDITIONS OF USE</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light leading-tight mt-3">
        Terms &amp; Conditions
      </h1>
      <p className="text-xs font-mono-tech text-[#5C657A] mt-3 uppercase tracking-[0.2em]">
        Effective: May 2026 · ProSource Labs LLC
      </p>

      <div className="mt-12 space-y-10 text-[#8A94A6] leading-relaxed">
        <section>
          <h2 className="font-heading text-xl text-white mb-3">Acceptance of Terms</h2>
          <p>
            These Terms &amp; Conditions ("Terms") govern your access to and use of
            <span className="text-white"> prosourcelabs.org </span> and any products, services, or
            communications offered by <span className="text-white">ProSource Labs LLC</span>
            ("ProSource Labs," "we," "us," or "our"). By browsing the site, creating an account,
            or placing an order, you confirm that you have read, understood, and agree to be bound
            by these Terms in full. If any part of these Terms is unacceptable to you, please
            discontinue use of the website immediately.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Eligibility</h2>
          <p>
            Access to this website is limited to individuals who are <span className="text-white">21 years of age or older</span>,
            are legally competent to enter binding contracts in their jurisdiction, and are
            qualified researchers, laboratory professionals, or institutional buyers. By
            registering, you represent that you satisfy each of these conditions. ProSource Labs
            may, at its discretion, request verification of identity, age, or institutional
            affiliation at any point and may refuse service if such verification is not provided.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Research Use Only</h2>
          <p>
            All compounds sold by ProSource Labs are <span className="text-white">manufactured and intended exclusively for in‑vitro laboratory research and analytical applications</span>.
            They are <em>not</em> intended for, and may not be used in, human consumption, animal
            consumption, therapeutic administration, diagnostic procedures, clinical treatment,
            cosmetic application, food production, or as components of medical devices.
            By placing an order you affirm that the products will be handled solely in accordance
            with this research-only purpose and all applicable laws.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">FDA &amp; Regulatory Status</h2>
          <p>
            Statements appearing on this website and on product packaging have not been evaluated
            by the U.S. Food and Drug Administration or any equivalent foreign regulatory
            authority. None of our compounds are approved for the diagnosis, treatment, cure, or
            prevention of any disease or condition. Customers are solely responsible for
            understanding and complying with the regulations applicable in their jurisdiction.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Orders &amp; Payment</h2>
          <p className="mb-3">
            All information you provide during checkout — including identity, billing details,
            and shipping address — must be accurate, current, and complete. ProSource Labs
            reserves the right to refuse, limit, cancel, or delay any order at our sole
            discretion, including (but not limited to) orders that we believe may violate these
            Terms, applicable law, or our internal compliance standards. Prices, product
            availability, promotions, and discount codes are subject to change without notice.
            See our separate <Link to="/refund-policy" className="text-[#00C3FF] hover:underline">Refund Policy</Link> for
            cancellation, return, and refund details.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Shipping</h2>
          <p>
            Quoted shipping times are estimates only and not guaranteed. ProSource Labs is not
            responsible for delays caused by carriers, customs authorities, weather events, or
            other circumstances outside our reasonable control. Customers must verify the
            accuracy of their shipping address prior to submission; once an order has been
            placed it cannot be re‑routed. Risk of loss and title pass to the customer when the
            carrier accepts the parcel.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Limited Product Warranty</h2>
          <p>
            ProSource Labs warrants that products will conform to the specifications stated on
            the product label at the time of shipment. Your sole and exclusive remedy for any
            non‑conformance is, at our option, replacement of the affected unit(s) or refund of
            the purchase price for those unit(s). EXCEPT FOR THIS LIMITED WARRANTY, ALL
            PRODUCTS ARE PROVIDED "AS IS" AND "AS AVAILABLE," AND PROSOURCE LABS DISCLAIMS ALL
            OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING ANY IMPLIED WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON‑INFRINGEMENT.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, ProSource Labs, its officers, members,
            employees, and suppliers shall not be liable for any indirect, incidental, special,
            consequential, exemplary, or punitive damages — including but not limited to lost
            profits, lost data, business interruption, or personal injury — arising out of or
            related to your use of this website or the products purchased through it. Our total
            aggregate liability for any claim, whether in contract, tort, or otherwise, shall not
            exceed the amount you actually paid us for the specific product giving rise to the
            claim.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless ProSource Labs and its affiliates,
            officers, members, employees, and agents from any and all claims, demands, losses,
            liabilities, damages, costs, or expenses (including reasonable attorneys' fees)
            arising out of or related to: (a) your handling, storage, use, distribution, or
            resale of any product; (b) your breach of these Terms; or (c) your violation of any
            applicable law or third‑party right.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">SMS &amp; Email Communications</h2>
          <p>
            By opting in through a sign‑up form, checkout flow, account registration, or
            customer‑support exchange, you consent to receive transactional and marketing
            communications from ProSource Labs via email and, where applicable, SMS. These may
            include order confirmations, shipping notifications, account updates, restock
            alerts, cart reminders, and promotional offers. Message frequency varies. Standard
            message and data rates may apply for SMS. You can unsubscribe at any time by
            replying STOP to a text message or by clicking the unsubscribe link in any
            marketing email. Consent to receive marketing messages is not a condition of
            purchase.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Privacy</h2>
          <p>
            Your use of this website is also governed by our Privacy Policy. ProSource Labs does
            not sell or share your personal information — including email address, phone number,
            or SMS opt‑in data — with unaffiliated third parties for their marketing purposes.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Intellectual Property</h2>
          <p>
            All content displayed on this website — including the ProSource Labs logo, name,
            trademarks, product photography, illustrations, copy, page layout, and source code —
            is the exclusive property of ProSource Labs LLC or its licensors and is protected
            under United States and international intellectual‑property law. You may not copy,
            reproduce, scrape, frame, mirror, reverse‑engineer, or otherwise exploit any portion
            of the website without our prior written consent.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Modification of Terms</h2>
          <p>
            ProSource Labs may revise these Terms at any time, with or without notice, by
            updating this page. Your continued use of the website following any change
            constitutes acceptance of the revised Terms. We recommend reviewing this page
            periodically.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the
            State in which ProSource Labs LLC is organized, without regard to its conflict‑of‑law
            principles. Any dispute arising under or related to these Terms shall be resolved
            exclusively in the state or federal courts located within that jurisdiction.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Contact</h2>
          <p>
            Questions about these Terms? Reach us at
            <a className="text-[#00C3FF] hover:underline ml-1" href="mailto:support@prosourcelabs.org">
              support@prosourcelabs.org
            </a>
            {" · "}<Link to="/contact" className="text-[#00C3FF] hover:underline">Contact form</Link>
          </p>
        </section>
      </div>
    </div>
  );
}
